import { AbstractForm } from '../abstract-form.model';
import { Regelstufe } from '../../../domain/model/regelstufe.enum';
import { Abregelungstyp, AbregelungstypItem } from '../../../domain/model/abregelungstyp.enum';
import { AbregelungKopplungsTyp } from '../../../domain/model/abregelung-kopplungs-typ.enum';
import { UntypedFormBuilder } from '@angular/forms';

interface AbregelungsinformationenAdressen {
    kopplungsTyp: AbregelungKopplungsTyp;
}

export abstract class AbregelungInformationen<T, U extends AbregelungsinformationenAdressen> extends AbstractForm<T> {

    // this is a map that assignes the option from the dropdown to all possible values
    public abregelungstypToKopplungsTypSelection: Map<Abregelungstyp, AbregelungKopplungsTyp[]> = new Map();
    public abregelungstypSelectionList: AbregelungstypItem[] = AbregelungstypItem.getAll();
    public abregelungstyp: Abregelungstyp;

    protected readonly DEFAULT_ABREGELUNGSTYP = Abregelungstyp.SOLLWERT;

    protected constructor(protected fb: UntypedFormBuilder) {
        super(fb);
        this.abregelungstypToKopplungsTypSelection[Abregelungstyp.SOLLWERT] = this.getTypSelectionList(
            AbregelungKopplungsTyp.SOLLWERT,
        );
        this.abregelungstypToKopplungsTypSelection[Abregelungstyp.AT_100_60_30_0] = this.getTypSelectionList(
            AbregelungKopplungsTyp.TYP_100,
            AbregelungKopplungsTyp.TYP_60,
            AbregelungKopplungsTyp.TYP_30,
            AbregelungKopplungsTyp.TYP_0,
        );
        this.abregelungstypToKopplungsTypSelection[Abregelungstyp.AT_100_60_0] = this.getTypSelectionList(
            AbregelungKopplungsTyp.TYP_100,
            AbregelungKopplungsTyp.TYP_60,
            AbregelungKopplungsTyp.TYP_0,
        );
        this.abregelungstypToKopplungsTypSelection[Abregelungstyp.AT_100_30_0] = this.getTypSelectionList(
            AbregelungKopplungsTyp.TYP_100,
            AbregelungKopplungsTyp.TYP_30,
            AbregelungKopplungsTyp.TYP_0,
        );
        this.abregelungstypToKopplungsTypSelection[Abregelungstyp.AT_100_0] = this.getTypSelectionList(
            AbregelungKopplungsTyp.TYP_100,
            AbregelungKopplungsTyp.TYP_0,
        );

        this.setDefaultSelectedTyp();
    }

    public determineTypeSelection(adressen: U[]): Abregelungstyp {
        let interpretedAbregelungstyp = adressen
            .filter(adresse => this.hasAddressValue(adresse))
            .map(adresse => {
                switch (adresse.kopplungsTyp) {
                    case AbregelungKopplungsTyp.SOLLWERT:
                        return 'SOLLWERT';
                    case AbregelungKopplungsTyp.TYP_100:
                        return '100';
                    case AbregelungKopplungsTyp.TYP_60:
                        return '60';
                    case AbregelungKopplungsTyp.TYP_30:
                        return '30';
                    case AbregelungKopplungsTyp.TYP_0:
                        return '0';
                    default:
                        return undefined;
                }
            })
            .filter(adresse => !!adresse) // filter WIRKLEISTUNG_P & BLINDLEISTUNG_Q
            .join(',');

        return Object.values(Abregelungstyp).includes(interpretedAbregelungstyp as Abregelungstyp) ?
            interpretedAbregelungstyp as Abregelungstyp :
            this.DEFAULT_ABREGELUNGSTYP;
    }

    public isVisible(kopplungstyp: AbregelungKopplungsTyp): boolean {
        for (const k of this.abregelungstypToKopplungsTypSelection[this.abregelungstyp]) {
            if (k === kopplungstyp) {
                return true;
            }
        }
        return false;
    }

    public isHidden(kopplungstyp: AbregelungKopplungsTyp): boolean {
        return !this.isVisible(kopplungstyp);
    }

    public updateAbregelungstypSelectionList(regelstufe: Regelstufe) {
        this.abregelungstypSelectionList = AbregelungstypItem.getAllForRegelstufe(regelstufe);
        if (!this.abregelungstypSelectionList.find(a => a.key === this.abregelungstyp)) {
            this.setDefaultSelectedTyp();
        }
    }

    protected abstract hasAddressValue(address: U): boolean

    protected getTypSelectionList(...args: AbregelungKopplungsTyp[]): AbregelungKopplungsTyp[] {
        const list = [
            AbregelungKopplungsTyp.BLINDLEISTUNG_Q,
            AbregelungKopplungsTyp.WIRKLEISTUNG_P,
        ];
        for (const arg of args) {
            list.push(arg);
        }
        return list;
    }

    protected setDefaultSelectedTyp() {
        this.abregelungstyp = this.DEFAULT_ABREGELUNGSTYP;
    }

}
